import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, HStack } from "@chakra-ui/layout";

import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { ErrorMessage } from "../../components/ErrorMessage/ErrorMessage";
import { LoadingIndicator } from "../../components/LoadingIndicator/LoadingIndicator";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import {
  Button,
  Text,
  SkeletonText,
  useDisclosure,
  Tag,
  TagLabel,
  TagRightIcon,
  Link as ChakraLink,
} from "@chakra-ui/react";
import {
  PlusIcon,
  FilterIcon,
  ClearFilterIcon,
  CloseCircleIcon,
} from "../../styles/icons";
import { useToast } from "../../hooks/useToast";
import { useAdminApiUpdateRequest } from "../../hooks/useApiUpdatedRequest";
import { usePaginatedAdminApiRequest } from "../../hooks/usePaginatedApiRequest";
import { UsersTable } from "../../components/Users/UsersTable";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import {
  UserFilterQueryModal,
  UserFilterType,
} from "../../components/Users/UserFilterQueryModal";
import { cloneDeep } from "lodash";
import { useSearchInput } from "../../hooks/useSearchInput";

export const Users = () => {
  const navigate = useNavigate();
  const [userFilter, setUserFilter] = useState<UserFilterType>();
  const userFilterQueryModal = useDisclosure();

  const { showErrorToast, showSuccessToast } = useToast();

  const {
    confirmationElement: enableDisableConfirmationElement,
    showConfirmationDialog: enableDisableShowConfirmationDialog,
  } = useConfirmationDialog();

  const { debouncedQuery, element: searchInputElement } = useSearchInput({
    placeholder: "Search user...",
  });

  const onSuccessUserFilterQuery = (query?: UserFilterType) => {
    setUserFilter(query);
    userFilterQueryModal.onClose();
  };

  const {
    items: users,
    refresh,
    isLoading,
    isLoadingPage,
    error,
    fetchNextPage,
  } = usePaginatedAdminApiRequest(
    // Request the first page
    (apiClient) =>
      apiClient.getUsers({
        q: debouncedQuery ? debouncedQuery : undefined,
        ...userFilter,
      }),
    // Request subsequent pages
    (apiClient, _, continuationToken) =>
      apiClient.getUsers({
        q: debouncedQuery ? debouncedQuery : undefined,
        ...userFilter,
        continuationToken,
      }),
    [debouncedQuery, userFilter]
  );

  const onClickNewUser = () => navigate(`/admin/users/new`);

  const disableUserRequest = useAdminApiUpdateRequest(
    (apiClient, userId: string) => apiClient.disableUser(userId),
    [users]
  );

  const enableUserRequest = useAdminApiUpdateRequest(
    (apiClient, userId: string) => apiClient.enableUser(userId),
    [users]
  );

  useEffect(() => {
    if (disableUserRequest.result) {
      if (disableUserRequest.result.ok) {
        showSuccessToast("User disabled.");
        refresh();
      } else {
        showErrorToast(
          disableUserRequest.result.message ?? "Failed to disable user."
        );
      }
    }
  }, [disableUserRequest.result, refresh, showSuccessToast, showErrorToast]);

  useEffect(() => {
    if (enableUserRequest.result) {
      if (enableUserRequest.result.ok) {
        showSuccessToast("User enabled.");
        refresh();
      } else {
        showErrorToast(
          enableUserRequest.result.message ?? "Failed to enable user."
        );
      }
    }
  }, [enableUserRequest.result, refresh, showSuccessToast, showErrorToast]);

  const onEnableDisableUser = useCallback(
    (user: CherryHubAdminApi.UserAccountSummaryItem) =>
      enableDisableShowConfirmationDialog(
        user.IsEnabled ? "Disable User?" : "Enable User?",
        `Are you sure you wish to ${
          user.IsEnabled ? "disable" : "enable"
        } this user: ${user.DisplayName ?? user.Email ?? user.Uid}?`,
        user.IsEnabled ? "Disable" : "Enable"
      ).then(({ ok }) => {
        if (ok) {
          user.IsEnabled
            ? disableUserRequest.request(user.Uid!)
            : enableUserRequest.request(user.Uid!);
        }
      }),
    [enableDisableShowConfirmationDialog]
  );

  const resetUserRequest = useAdminApiUpdateRequest(
    (apiClient, userId: string) => apiClient.resetUserPassword(userId),
    [users]
  );

  useEffect(() => {
    if (resetUserRequest.result) {
      if (resetUserRequest.result.ok) {
        showSuccessToast("Reset password success.");
        refresh();
      } else {
        showErrorToast(
          resetUserRequest.result.message ?? "Failed to reset password user."
        );
      }
    }
  }, [resetUserRequest.result, refresh, showSuccessToast, showErrorToast]);

  const onResetPasswordUser = useCallback(
    (user: CherryHubAdminApi.UserAccountSummaryItem) =>
      enableDisableShowConfirmationDialog(
        "Send Reset User Password?",
        `Are you sure you wish to reset this user: ${
          user.DisplayName ?? user.Email ?? user.Uid
        } password?`,
        "Send Reset Password"
      ).then(({ ok }) => {
        if (ok) {
          resetUserRequest.request(user.Uid!);
        }
      }),
    [enableDisableShowConfirmationDialog]
  );

  const closeUserFilter = (key: string) => {
    const uFilter = cloneDeep(userFilter) as { [key: string]: any };
    delete uFilter[key];
    setUserFilter(uFilter as UserFilterType);
  };

  const clearUserFilter = () => {
    setUserFilter({});
  };

  return (
    <>
      <PageHeading>
        <PageHeading.Title>Users</PageHeading.Title>
      </PageHeading>
      <PageContent>
        <HStack w="100%">
          <Box flex="1" maxWidth="400px">
            {searchInputElement}
          </Box>
          <Box flexGrow={1}>
            <Button
              onClick={userFilterQueryModal.onOpen}
              alignSelf="flex-end"
              colorScheme="cherryButton"
              leftIcon={<FilterIcon />}
            >
              Filters
            </Button>
          </Box>
          <Box marginLeft="auto">
            <Button
              leftIcon={<PlusIcon />}
              onClick={onClickNewUser}
              colorScheme="cherryButton"
            >
              New User
            </Button>
          </Box>
        </HStack>
        <HStack w="100%" alignItems="flex-start" color="gray.600">
          {(userFilter && Object.keys(userFilter).length && (
            <>
              <Text>
                Filter |{" "}
                <ChakraLink onClick={clearUserFilter}>
                  <ClearFilterIcon /> Clear{" "}
                </ChakraLink>{" "}
                :{" "}
              </Text>
              {Object.entries(userFilter as { [key: string]: any }).map(
                ([key, value]) => (
                  <Tag
                    size="sm"
                    key={key}
                    cursor="pointer"
                    colorScheme="cherryButton"
                    color="white"
                    onClick={() => closeUserFilter(key)}
                  >
                    <TagLabel>
                      {key} : {value}
                    </TagLabel>
                    <TagRightIcon as={CloseCircleIcon} />
                  </Tag>
                )
              )}
            </>
          )) || (
            <>
              <Text>Filter :</Text>
              <Text fontStyle="italic">No Filter Applied</Text>
            </>
          )}
        </HStack>

        <UsersTable
          users={users}
          isLoading={isLoading}
          disableActions={
            enableUserRequest.isLoading ||
            disableUserRequest.isLoading ||
            resetUserRequest.isLoading
          }
          debouncedQuery={debouncedQuery}
          onDisableUser={onEnableDisableUser}
          onEnableUser={onEnableDisableUser}
          onResetPasswordUser={onResetPasswordUser}
        />
        {fetchNextPage && !isLoading && (
          <Box w="100%">
            {!isLoadingPage && (
              <Button colorScheme="cherryButton" onClick={fetchNextPage}>
                Load more...
              </Button>
            )}
            {isLoadingPage && (
              <HStack width="100%" boxShadow="lg">
                <Box width="20%" padding="4" pr="0">
                  <SkeletonText mt="4" noOfLines={4} spacing="4" />
                </Box>
                <Box width="30%" padding="4" pr="0">
                  <SkeletonText mt="4" noOfLines={4} spacing="4" />
                </Box>
                <Box width="40%" padding="4" pr="0">
                  <SkeletonText mt="4" noOfLines={4} spacing="4" />
                </Box>
                <Box width="5%" pt="4" pb="4">
                  <SkeletonText mt="4" noOfLines={4} spacing="4" />
                </Box>
                <Box width="5%" pt="4" pb="4" pr="4">
                  <SkeletonText mt="4" noOfLines={4} spacing="4" />
                </Box>
              </HStack>
            )}
          </Box>
        )}
        {error != null && (
          <ErrorMessage>
            {error ?? "An error was encountered while searching users."}
          </ErrorMessage>
        )}
        {(!users || isLoading) && <LoadingIndicator />}
        {enableDisableConfirmationElement}
        <UserFilterQueryModal
          size="4xl"
          {...userFilterQueryModal}
          onSuccess={onSuccessUserFilterQuery}
          userQueryFilter={userFilter}
        />
      </PageContent>
    </>
  );
};
